<template>
    <div v-if="loading">
        {{ loadingMessage }}
    </div>
    <div v-if="!loading" class="content">
        <div class="container-fluid">
            <!-- Titre & breadcrumb -->
            <div class="page-heading row justify-content-between align-items-center py-3">
                <div class="col-lg-4">
                    <h4 class="page-title">{{ $t('members.title') }}</h4>
                </div>
            </div>
            <!-- Table [start] -->
            <div class="card">
                <div class="card-header">
                    <div class="row align-items-center">
                        <div class="col-md-4">
                            <h4 class="header-title">{{ $t('members.listTitle') }}</h4>
                        </div>
                        <div class="col-md-8 mt-2 mt-md-0 text-end">
                            <div class="d-flex">
                                <input type="text" id="searchTrigger" class="form-control form-control-sm search-trigger" :placeholder="$t('common.search')" aria-controls="users-table" maxlength="64">
                                <button type="button" class="btn ms-2 btn-primary" @click="addMember()">
                                    <i class="mdi mdi-plus"></i><span class="d-none d-xxl-inline-block">{{ $t('common.add') }}</span>
                                </button>
                                <button type="button" class="btn ms-2 btn-soft-primary" @click="inviteMember()">
                                    <i class="mdi mdi-email-plus me-xxl-1"></i><span class="d-none d-xxl-inline-block">{{ $t('members.invite') }}</span>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="card-body border-top">
                    <CustomDataTable ref="datatable" tableId="team-members-table" :columns="columns" :data="members" :emptyTableLabel="$t('members.emptyTable')" :rowClick="rowClick" />
                </div>
                <!-- Table [end] -->
            </div>
        </div>
    </div>
    <ConfirmationModal ref="confirmDeleteModal" :title="$t('common.confirmDelete')" :question="$t('members.confirmDeleteQuestion')" type="danger" :button1="$t('common.cancel')" :button2="$t('common.delete')" :action="removeMember" />
    <InviteModal ref="inviteModal" />
</template>

<script>
import { useUserStore } from '@/stores/userStore';
import { showToast } from '@/services/toast'
import { useRouter } from 'vue-router';
import { onMounted, ref } from "vue";
import { useI18n } from 'vue-i18n';
import CustomDataTable from '@/components/customDatatable.vue';
import ConfirmationModal from '@/modals/confirmationModal.vue';
import InviteModal from '@/modals/inviteModal.vue';

export default {
    name: 'MembersPage',
    components: {
        ConfirmationModal,
        CustomDataTable,
        InviteModal,
    },
    setup() {
        const router = useRouter();
        const userStore = useUserStore();
        const { t } = useI18n();
        const members = ref([]);
        const loading = ref(true);
        const loadingMessage = ref(t('common.loading'));
        const inviteModal = ref(null);
        const confirmDeleteModal = ref(null);
        const datatable = ref(null);

        const columns = [
            {
                id: 0,
                title: t('members.name'),
                data: 'full_name',
                orderable: true,
                searchable: true,
                visible: true,
                className: 'cell-name',
                render: (data, type, row) => {
                    if (type === 'sort' || type === 'filter') {
                        return data;
                    }
                    if(row.picture) {
                        return `
                            <div class="d-flex align-items-center">
                                <img src="${row.picture}" alt="image" class="img-fluid rounded-circle avatar-sm me-2">
                                <a href="#" class="link row-click" row-action='edit' row-id="${row.id}">${data}</a>
                            </div>
                        `;
                    } else {
                        return `
                            <div class="d-flex align-items-center">
                                <div class="avatar-sm rounded-circle me-2">
                                    <span class="avatar-title rounded-circle bg-info-lighten text-info">${row.initials}</span>
                                </div>
                                <a href="#" class="link row-click" row-action='edit' row-id="${row.id}">${data}</a>
                            </div>
                        `;
                    }
                }
            },
            {
                id: 1,
                data: null,
                title: t('common.actions'),
                orderable: false,
                searchable: false,
                className: 'table-action',
                render: function (data, type, row) {
                    return `
                        <button class="btn btn-primary btn-sm me-1 d-none d-xxl-inline-block row-click" row-action='edit' row-id="${row.id}">
                            <i class="mdi mdi-pencil me-1"></i>${t('common.edit')}
                        </button>
                        <button class="btn btn-danger btn-sm d-none d-xxl-inline-block row-click" row-action='delete' row-id="${row.id}">
                            <i class="mdi mdi-delete me-1"></i>${t('common.delete')}
                        </button>
                        <button class="btn btn-sm btn-light dropdown-toggle arrow-none border d-xxl-none" data-bs-toggle="dropdown" aria-expanded="false">
                            <i class="mdi mdi-dots-vertical"></i>
                        </button>
                        <div class="dropdown-menu dropdown-menu-end">
                            <button class="dropdown-item row-click" row-action='edit' row-id="${row.id}">
                                <i class="mdi mdi-pencil me-1"></i>${t('common.edit')}
                            </button>
                            <hr class="my-0">
                            <button class="dropdown-item text-danger row-click" row-action='delete' row-id="${row.id}">
                                <i class="mdi mdi-delete me-1"></i>${t('common.delete')}
                            </button>
                        </div>
                    `;
                }
            }
        ];

        onMounted(async () => {
            try {
                members.value = await userStore.loadMembers();
                loading.value = false;
            } catch(error) {
                loadingMessage.value = error;
            }
        });

        function rowClick(action, id) {
            switch(action) {
                case 'edit':
                    editMember(id);
                    break;
                case 'delete':
                    removeMemberConfirm(id);
                    break;
            }
        }

        function removeMemberConfirm(id) {
            confirmDeleteModal.value.show(id);
        }

        async function removeMember(id) {
            try {
                await userStore.removeMember(id);
                members.value = members.value.filter(m => m.id != id);
                datatable.value.refresh(members.value);
                showToast('success', t('members.removeSuccess'));
            } catch(error) {
                showToast('error', error);
            }
        }

        function inviteMember() {
            inviteModal.value.show();
        }

        function addMember() {
            editMember(0);
        }

        function editMember(id) {
            router.push({ path: '/member', query: { id: id } });
        }

        return {
            inviteMember,
            addMember,
            removeMember,
            members,
            loading,
            loadingMessage,
            inviteModal,
            confirmDeleteModal,
            datatable,
            rowClick,
            columns,
        };
    }
};
</script>
